<template>
  <div class="space-y-4">
    <div class="flex flex-wrap gap-2">
      <button
        type="button"
        class="rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
        :class="
          isDaily
            ? 'bg-sky-500 text-white shadow-sm'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
        "
        @click="setMode('daily')">
        {{ tt('Daily', 'Dnevno') }}
      </button>
      <button
        type="button"
        class="rounded-full px-3 py-1.5 text-xs font-semibold transition-colors"
        :class="
          !isDaily
            ? 'bg-sky-500 text-white shadow-sm'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
        "
        @click="setMode('weekly')">
        {{ tt('Weekly', 'Tedensko') }}
      </button>
    </div>

    <div
      v-if="isDaily"
      class="space-y-3 rounded-2xl border border-gray-200 bg-white/75 p-4 dark:border-gray-700 dark:bg-gray-900/50">
      <div v-for="(pair, index) in dailyPairs" :key="`daily-${index}`" class="grid gap-3 sm:grid-cols-[1fr,1fr,auto]">
        <TimeSwitchEdgeInput
          :label="tt('On time', 'Vklop ob')"
          :model-value="pair.on_time"
          @update:model-value="updateDailyPair(index, 'on_time', $event)" />

        <TimeSwitchEdgeInput
          :label="tt('Off time', 'Izklop ob')"
          :model-value="pair.off_time"
          @update:model-value="updateDailyPair(index, 'off_time', $event)" />

        <div class="flex items-end">
          <button
            type="button"
            class="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition hover:border-rose-300 hover:text-rose-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:border-rose-500 dark:hover:text-rose-300"
            @click="removeDailyPair(index)">
            <ControlIcon name="close" size="1rem" />
          </button>
        </div>
      </div>

      <button
        type="button"
        class="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300 dark:hover:border-sky-500/50 dark:hover:bg-sky-500/15"
        @click="addDailyPair">
        <ControlIcon name="plus" size="1rem" />
        {{ tt('Add time pair', 'Dodaj par časov') }}
      </button>
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="day in weeklyDays"
        :key="day.day"
        class="rounded-2xl border border-gray-200 bg-white/75 px-3 py-3 dark:border-gray-700 dark:bg-gray-900/50">
        <div class="flex items-start gap-3">
          <button
            type="button"
            class="inline-flex h-10 min-w-[3.25rem] items-center justify-center rounded-full px-3 text-xs font-semibold transition-colors"
            :class="
              day.enabled
                ? 'bg-sky-500 text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700'
            "
            @click="toggleDay(day.day)">
            {{ tt(day.shortLabel.en, day.shortLabel.sl) }}
          </button>

          <div class="min-w-0 flex-1 space-y-3">
            <template v-if="day.enabled">
              <div
                v-for="(pair, index) in day.pairs"
                :key="`${day.day}-${index}`"
                class="grid gap-3 sm:grid-cols-[1fr,1fr,auto]">
                <TimeSwitchEdgeInput
                  :label="tt('On', 'Vklop')"
                  :model-value="pair.on_time"
                  @update:model-value="updateWeeklyPair(day.day, index, 'on_time', $event)" />

                <TimeSwitchEdgeInput
                  :label="tt('Off', 'Izklop')"
                  :model-value="pair.off_time"
                  @update:model-value="updateWeeklyPair(day.day, index, 'off_time', $event)" />

                <div class="flex items-end">
                  <button
                    type="button"
                    class="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 transition hover:border-rose-300 hover:text-rose-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 dark:hover:border-rose-500 dark:hover:text-rose-300"
                    @click="removeWeeklyPair(day.day, index)">
                    <ControlIcon name="close" size="1rem" />
                  </button>
                </div>
              </div>

              <button
                type="button"
                class="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 transition hover:border-sky-300 hover:bg-sky-100 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300 dark:hover:border-sky-500/50 dark:hover:bg-sky-500/15"
                @click="addWeeklyPair(day.day)">
                <ControlIcon name="plus" size="1rem" />
                {{ tt('Add pair', 'Dodaj par') }}
              </button>
            </template>

            <p v-else class="text-sm text-gray-500 dark:text-gray-400">
              {{ tt('Off for this day.', 'Ta dan izklopljeno.') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="scheduleErrors.length"
      class="space-y-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
      <p class="text-xs font-semibold uppercase tracking-[0.18em]">{{ tt('Schedule issues', 'Težave z urnikom') }}</p>
      <p v-for="error in scheduleErrors" :key="error">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import TimeSwitchEdgeInput from './TimeSwitchEdgeInput.vue';
import {
  buildDailyScheduleFromPairs,
  buildWeeklyScheduleFromDays,
  getDailySchedulePairs,
  getTimeSwitchScheduleErrors,
  getWeeklyScheduleDays,
  type TimeSwitchSchedulePair,
  type TimeSwitchTime,
} from '../../utils/switchAutomation';
import { tt } from '../../utils/consoleText';
import { omitKeys } from '../../utils/omitKeys';

interface Props {
  modelValue: Record<string, unknown>;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'update:modelValue': [value: Record<string, unknown>];
}>();

const isDaily = computed(() => props.modelValue.schedule_mode !== 'weekly');
const dailyPairs = computed(() => getDailySchedulePairs(props.modelValue));
const weeklyDays = computed(() => getWeeklyScheduleDays(props.modelValue));
const scheduleErrors = computed(() => getTimeSwitchScheduleErrors(props.modelValue));

function createDefaultPair(): TimeSwitchSchedulePair {
  return {
    on_time: { type: 'time', time: '08:00' },
    off_time: { type: 'time', time: '22:00' },
  };
}

function updateConfig(patch: Record<string, unknown>) {
  const rest = omitKeys(props.modelValue, ['daily_on_time', 'daily_off_time']);

  emit('update:modelValue', {
    ...rest,
    ...patch,
  });
}

function setMode(mode: 'daily' | 'weekly') {
  if (mode === 'daily') {
    updateConfig({
      schedule_mode: 'daily',
      daily_schedule: buildDailyScheduleFromPairs(dailyPairs.value),
    });
    return;
  }

  updateConfig({
    schedule_mode: 'weekly',
    weekly_schedule: buildWeeklyScheduleFromDays(weeklyDays.value),
  });
}

function addDailyPair() {
  updateDailyPairs([...dailyPairs.value, createDefaultPair()]);
}

function removeDailyPair(index: number) {
  updateDailyPairs(dailyPairs.value.filter((_, pairIndex) => pairIndex !== index));
}

function updateDailyPair(index: number, key: keyof TimeSwitchSchedulePair, value: TimeSwitchTime) {
  const pairs = dailyPairs.value.map((pair, pairIndex) => (pairIndex === index ? { ...pair, [key]: value } : pair));

  updateDailyPairs(pairs);
}

function updateDailyPairs(pairs: TimeSwitchSchedulePair[]) {
  updateConfig({
    schedule_mode: 'daily',
    daily_schedule: buildDailyScheduleFromPairs(pairs),
  });
}

function toggleDay(dayNumber: number) {
  const current = weeklyDays.value.find((day) => day.day === dayNumber);
  if (!current) return;

  const days = weeklyDays.value.map((day) => {
    if (day.day !== dayNumber) return day;
    if (day.enabled) {
      return { ...day, enabled: false, pairs: [] };
    }

    return {
      ...day,
      enabled: true,
      pairs: day.pairs.length > 0 ? day.pairs : [createDefaultPair()],
    };
  });

  updateWeeklyDays(days);
}

function addWeeklyPair(dayNumber: number) {
  const days = weeklyDays.value.map((day) =>
    day.day === dayNumber ? { ...day, enabled: true, pairs: [...day.pairs, createDefaultPair()] } : day,
  );

  updateWeeklyDays(days);
}

function removeWeeklyPair(dayNumber: number, pairIndex: number) {
  const days = weeklyDays.value.map((day) => {
    if (day.day !== dayNumber) return day;

    const pairs = day.pairs.filter((_, index) => index !== pairIndex);
    return {
      ...day,
      enabled: pairs.length > 0,
      pairs,
    };
  });

  updateWeeklyDays(days);
}

function updateWeeklyPair(
  dayNumber: number,
  pairIndex: number,
  key: keyof TimeSwitchSchedulePair,
  value: TimeSwitchTime,
) {
  const days = weeklyDays.value.map((day) => {
    if (day.day !== dayNumber) return day;

    return {
      ...day,
      pairs: day.pairs.map((pair, index) => (index === pairIndex ? { ...pair, [key]: value } : pair)),
    };
  });

  updateWeeklyDays(days);
}

function updateWeeklyDays(days: ReturnType<typeof getWeeklyScheduleDays>) {
  const normalizedDays = days.map((day) => ({
    ...day,
    pairs: day.enabled ? day.pairs : [],
  }));

  updateConfig({
    schedule_mode: 'weekly',
    weekly_schedule: buildWeeklyScheduleFromDays(normalizedDays),
  });
}
</script>
